<template>
	<OnboardingStep :commit="commit" :form-id="formId" @skip="event = null">
		<template #top>
			<p v-if="currentPathName === 'hire-me'" class="center">
				<BaseButton class="button-inline" @click="restartOnboarding">
					Not looking to book someone for a job right now? Click here.
				</BaseButton>
			</p>
		</template>

		<template #heading>
			<h1 class="onboarding-step-heading">Tell Us About Your Job</h1>
		</template>

		<template #default="{ next }">
			<div class="card-v2">
				<SearchForm v-model="event" no-filters v-bind="forward" :confirm-discard="false" :id="formId" :verb="null" ref="searchForm" @save="next" />
			</div>
		</template>
	</OnboardingStep>
</template>

<script>
import SearchForm from '@/components/forms/SearchForm';
import { toString } from '@/helpers/convert';
import onboardingHelper from '@/helpers/onboarding';
import form from '@/mixins/form';
import onboarding from '@/mixins/onboarding';
import JobEvent from '@/models/JobEvent';
import Role from '@/models/Role';
import { toTimestamp } from '@/plugins/time';

export default {
  name: 'OnboardingEvent',
  mixins: [form, onboarding],
  components: { SearchForm },
  onboardingStep: {
    isCompleted: ({ event, hiringUserProfile }) => {
      if (!event) {
        /**
         * Event is optional unless the user clicked on a "Hire Me" button on someone's profile.
         */
        return !hiringUserProfile;
      }
      return (
        toString(event.name).trim() &&
        toTimestamp(event.startedAt) != null &&
        event.locations.length &&
        event.jobs.some((job) => job.positionId !== Role.studio.id)
      );
    },
    isOptional: ({ hiringUserProfile }) => !hiringUserProfile,
  },
  computed: {
    /**
     * @returns {string | null}
     */
    currentPathName() {
      /** @type {import('@/helpers/onboarding').OnboardingPath | null} */
      const path = this.$store.getters['onboarding/currentPath'];
      return path?.name || null;
    },
    event: {
      /**
       * @returns {globalThis.JobEvent}
       */
      get() {
        return this.$store.getters['onboarding/event'] || new JobEvent();
      },
      /**
       * @param {globalThis.JobEvent} event
       * @returns {void}
       */
      set(event) {
        this.$store.commit('onboarding/setEvent', event);
      },
    },
    /**
     * @returns {string}
     */
    formId() {
      return 'onboarding-step-form';
    },
    /**
     * @returns {boolean}
     */
    isLastStep() {
      return this.currentStepIndex === this.steps.length - 1;
    },
  },
  methods: {
    /**
     * @returns {Promise<boolean | void>}
     */
    async commit() {
      const store = this.$store;
      let { event } = this;
      if (this.hiringUserProfile) {
        // This is the "hire me" onboarding flow where the user hasn't been created yet.
        // Get the total number of matching candidates and store this number so it can be shown
        // above the "Create Account" form on the next step.
        const count = await store.dispatch('jobs/getMatchingCandidateCount', event);
        store.commit('onboarding/setCandidateCount', count);
      } else if (event) {
        event = await store.dispatch(`events/${event.id ? 'update' : 'create'}Event`, event);
        this.event = event;
        store.commit('events/setSearchEvent', event);
        store.commit('events/setSearchQueue', null);
      }
      return true;
    },
    /**
     * @returns {void}
     */
    async restartOnboarding() {
      const store = this.$store;
      store.commit('onboarding/setHiringUserProfile', null);
      store.commit('onboarding/setPlan', null);
      const step = await onboardingHelper.inferCurrentStep();
      await this.$router.replace({ name: step?.routeName || 'onboarding' });
    },
  },
  created() {
    const store = this.$store;
    if (!store.getters['onboarding/event']) {
      store.commit('onboarding/setEvent', new JobEvent());
    }
  },
};
</script>
